/**
 * Helpers pour les dictionnaires de Pré-chiffrage
 * Fournit des fonctions utilitaires pour accéder aux dictionnaires work_field, work_type et room_type
 */

import dexieService from '@/services/dexieService'

/**
 * Cache en mémoire des dictionnaires de pré-chiffrage pour éviter les accès répétés à IndexedDB
 */
const cache = {
  workField: null,
  workType: null,
  roomType: null
}

/**
 * Récupère le dictionnaire work_field depuis IndexedDB avec cache
 * @returns {Promise<Object>} Dictionnaire clé-valeur
 */
export async function getWorkFieldDictionary() {
  if (cache.workField) {
    return cache.workField
  }
  
  try {
    const dict = await dexieService.getDictionary('work_field')
    cache.workField = dict || {}
    return cache.workField
  } catch (error) {
    console.error('Erreur lors du chargement du dictionnaire work_field:', error)
    return {}
  }
}

/**
 * Récupère le dictionnaire work_type depuis IndexedDB avec cache
 * @returns {Promise<Object>} Dictionnaire clé-valeur
 */
export async function getWorkTypeDictionary() {
  if (cache.workType) {
    return cache.workType
  }
  
  try {
    const dict = await dexieService.getDictionary('work_type')
    cache.workType = dict || {}
    return cache.workType
  } catch (error) {
    console.error('Erreur lors du chargement du dictionnaire work_type:', error)
    return {}
  }
}

/**
 * Récupère le dictionnaire room_type depuis IndexedDB avec cache
 * @returns {Promise<Object>} Dictionnaire clé-valeur
 */
export async function getRoomTypeDictionary() {
  if (cache.roomType) {
    return cache.roomType
  }
  
  try {
    const dict = await dexieService.getDictionary('room_type')
    cache.roomType = dict || {}
    return cache.roomType
  } catch (error) {
    console.error('Erreur lors du chargement du dictionnaire room_type:', error)
    return {}
  }
}

/**
 * Récupère le label d'un champ de travaux
 * @param {string} fieldKey - Clé du champ (ex: 'nature-sol-actuel')
 * @returns {Promise<string>} Label du champ
 */
export async function getWorkFieldLabel(fieldKey) {
  const dict = await getWorkFieldDictionary()
  return dict[fieldKey] || fieldKey
}

/**
 * Récupère le label d'un type de travaux
 * @param {string} workTypeKey - Clé du type de travaux (ex: 'sols')
 * @returns {Promise<string>} Label du type de travaux
 */
export async function getWorkTypeLabel(workTypeKey) {
  const dict = await getWorkTypeDictionary()
  return dict[workTypeKey] || workTypeKey
}

/**
 * Récupère le label d'un type de pièce
 * @param {string} roomTypeKey - Clé du type de pièce (ex: 'sejour')
 * @returns {Promise<string>} Label du type de pièce
 */
export async function getRoomTypeLabel(roomTypeKey) {
  const dict = await getRoomTypeDictionary()
  return dict[roomTypeKey] || roomTypeKey
}

/**
 * Récupère les valeurs disponibles pour un champ depuis IndexDB
 * Cette méthode récupère les métadonnées enrichies incluant availableValues
 * @returns {Promise<Array>} Liste des champs avec leurs métadonnées
 */
export async function getWorkFieldsWithValues() {
  try {
    const fields = await dexieService.getDictionary('prechiffrage_work_fields')
    return Array.isArray(fields) ? fields : []
  } catch (error) {
    console.error('Erreur lors du chargement des champs de travaux avec valeurs:', error)
    return []
  }
}

/**
 * Récupère les types de travaux avec leurs champs associés depuis IndexDB
 * @returns {Promise<Array>} Liste des types de travaux avec leurs champs
 */
export async function getWorkTypesWithFields() {
  try {
    const workTypes = await dexieService.getDictionary('prechiffrage_work_types')
    return Array.isArray(workTypes) ? workTypes : []
  } catch (error) {
    console.error('Erreur lors du chargement des types de travaux avec champs:', error)
    return []
  }
}

/**
 * Récupère les valeurs disponibles pour un champ spécifique
 * @param {string} fieldKey - Clé du champ (ex: 'nature-sol-actuel')
 * @returns {Promise<Object|null>} Objet {code: label} des valeurs disponibles ou null
 */
export async function getAvailableValuesForField(fieldKey) {
  try {
    const fields = await getWorkFieldsWithValues()
    const field = fields.find(f => f.value === fieldKey)
    return field?.availableValues || null
  } catch (error) {
    console.error(`Erreur lors de la récupération des valeurs pour le champ ${fieldKey}:`, error)
    return null
  }
}

/**
 * Récupère tous les champs d'un type de travaux avec leurs métadonnées
 * @param {string} workTypeKey - Clé du type de travaux (ex: 'sols')
 * @returns {Promise<Object>} Objet {fieldKey: {label, values, required}}
 */
export async function getFieldsByWorkType(workTypeKey) {
  try {
    const workTypes = await getWorkTypesWithFields()
    const workType = workTypes.find(wt => wt.value === workTypeKey)
    return workType?.fields || {}
  } catch (error) {
    console.error(`Erreur lors de la récupération des champs pour le type ${workTypeKey}:`, error)
    return {}
  }
}

/**
 * Formate un champ de pré-chiffrage pour l'affichage
 * @param {string} fieldKey - Clé du champ
 * @param {any} value - Valeur du champ
 * @returns {Promise<Object>} Objet avec fieldLabel et displayValue
 */
export async function formatPreChiffrageField(fieldKey, value) {
  const fieldLabel = await getWorkFieldLabel(fieldKey)
  
  // Si le champ a des valeurs disponibles, essayer de trouver le label
  const availableValues = await getAvailableValuesForField(fieldKey)
  let displayValue = value
  
  if (availableValues && value && availableValues[value]) {
    displayValue = availableValues[value]
  }
  
  return {
    fieldLabel,
    displayValue
  }
}

/**
 * Invalide le cache des dictionnaires
 * Utile après un rechargement des dictionnaires depuis l'API
 */
export function clearPreChiffrageCache() {
  cache.workField = null
  cache.workType = null
  cache.roomType = null
}

/**
 * Pré-charge tous les dictionnaires de pré-chiffrage en cache
 * @returns {Promise<boolean>} true si succès
 */
export async function preloadPreChiffrageDictionaries() {
  try {
    await Promise.all([
      getWorkFieldDictionary(),
      getWorkTypeDictionary(),
      getRoomTypeDictionary()
    ])
    return true
  } catch (error) {
    console.error('Erreur lors du pré-chargement des dictionnaires de pré-chiffrage:', error)
    return false
  }
}

/**
 * Vérifie si les dictionnaires de pré-chiffrage sont chargés dans IndexedDB
 * @returns {Promise<boolean>} true si tous les dictionnaires sont présents
 */
export async function arePreChiffrageDictionariesLoaded() {
  try {
    const [workField, workType, roomType] = await Promise.all([
      dexieService.getDictionary('work_field'),
      dexieService.getDictionary('work_type'),
      dexieService.getDictionary('room_type')
    ])
    
    return (
      workField && Object.keys(workField).length > 0 &&
      workType && Object.keys(workType).length > 0 &&
      roomType && Object.keys(roomType).length > 0
    )
  } catch (error) {
    console.error('Erreur lors de la vérification des dictionnaires de pré-chiffrage:', error)
    return false
  }
}

export default {
  getWorkFieldDictionary,
  getWorkTypeDictionary,
  getRoomTypeDictionary,
  getWorkFieldLabel,
  getWorkTypeLabel,
  getRoomTypeLabel,
  getWorkFieldsWithValues,
  getWorkTypesWithFields,
  getAvailableValuesForField,
  getFieldsByWorkType,
  formatPreChiffrageField,
  clearPreChiffrageCache,
  preloadPreChiffrageDictionaries,
  arePreChiffrageDictionariesLoaded
}
